import { Container, Graphics, Text } from 'pixi.js';
import { PlayerSide } from '../types/game.types';
import { Button } from './Button';
import { lerp } from '../utils/animation';
import { FONT_DISPLAY, FONT_UI, PALETTE } from './theme';

export class GameOverOverlay extends Container {
  private backdrop: Graphics;
  private glow: Graphics;
  private winnerText: Text;
  private detailText: Text;
  private playAgainButton: Button;
  private onPlayAgainCallback: (() => void) | null = null;
  private targetAlpha: number = 0;

  constructor(width: number, height: number) {
    super();

    this.visible = false;
    this.alpha = 0;

    this.backdrop = new Graphics();
    this.backdrop.beginFill(PALETTE.night, 0.84);
    this.backdrop.drawRect(0, 0, width, height);
    this.backdrop.endFill();
    this.addChild(this.backdrop);

    this.glow = new Graphics();
    this.glow.beginFill(PALETTE.accent, 0.12);
    this.glow.drawCircle(width / 2, height / 2 - 60, 220);
    this.glow.endFill();
    this.addChild(this.glow);

    this.winnerText = new Text('', {
      fontFamily: FONT_DISPLAY,
      fontSize: 70,
      fill: PALETTE.ink,
      fontWeight: 'bold',
      letterSpacing: 1.2,
    });
    this.winnerText.anchor.set(0.5);
    this.winnerText.x = width / 2;
    this.winnerText.y = height / 2 - 72;
    this.addChild(this.winnerText);

    this.detailText = new Text('Reset the orchard and open a fresh round.', {
      fontFamily: FONT_UI,
      fontSize: 20,
      fill: PALETTE.mist,
      letterSpacing: 0.6,
    });
    this.detailText.anchor.set(0.5);
    this.detailText.x = width / 2;
    this.detailText.y = height / 2 - 8;
    this.addChild(this.detailText);

    this.playAgainButton = new Button({
      text: 'Play again',
      width: 228,
      height: 62,
      backgroundColor: PALETTE.accent,
      hoverColor: PALETTE.accentHover,
      fontSize: 22,
      borderColor: PALETTE.ink,
    });
    this.playAgainButton.x = width / 2 - 114;
    this.playAgainButton.y = height / 2 + 44;
    this.playAgainButton.onClick(() => {
      if (this.onPlayAgainCallback) this.onPlayAgainCallback();
    });
    this.addChild(this.playAgainButton);
  }

  show(winner: PlayerSide): void {
    this.winnerText.text =
      winner === PlayerSide.KIWI ? 'Kiwis win the orchard' : 'Coconuts take the field';
    this.visible = true;
    this.targetAlpha = 1;
  }

  hide(): void {
    this.targetAlpha = 0;
  }

  onPlayAgain(callback: () => void): void {
    this.onPlayAgainCallback = callback;
  }

  animate(delta: number): void {
    this.alpha = lerp(this.alpha, this.targetAlpha, Math.min(delta * 8, 1));

    if (this.alpha <= 0.02 && this.targetAlpha === 0) {
      this.visible = false;
      return;
    }

    this.visible = true;
    const scale = 0.96 + this.alpha * 0.04;
    this.winnerText.scale.set(scale);
    this.detailText.alpha = this.alpha;
    this.playAgainButton.alpha = this.alpha;
  }
}
